/**
 * Expectation Match penalty step `P` — Configuration (docs/specs/discipline-rating-fit-score.md
 * Story E4). Per Engagement Standard Tier, editable by RM/CEO (both hold
 * `manageConfiguration`); default 25; integer > 0 validated; server-audited.
 */

import { Pencil } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type TierExpectationPenaltyStep,
  useSetTierExpectationPenaltyStep,
  useTierExpectationPenaltyStep,
} from "@/hooks/use-engagement-standard-tiers";
import type { TierWithDefaultShape } from "@/hooks/use-pod-shapes";
import type { NymblRole } from "@/lib/auth";
import { useSession } from "@/lib/auth-client";
import { PERMISSION_MATRIX } from "@/lib/permissions";

function hasCapability(
  role: string | null | undefined,
  capability: keyof (typeof PERMISSION_MATRIX)[NymblRole],
): boolean {
  if (!role) return false;
  const matrix = PERMISSION_MATRIX[role as NymblRole];
  if (!matrix) return false;
  return matrix[capability] !== undefined;
}

function TierPenaltyStepEditDialog({
  open,
  onOpenChange,
  tier,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tier: TierExpectationPenaltyStep;
}) {
  const setStep = useSetTierExpectationPenaltyStep(tier.id);
  const [draft, setDraft] = useState(String(tier.expectationPenaltyStep));

  const parsedDraft = Number(draft);
  const isValidDraft = Number.isInteger(parsedDraft) && parsedDraft > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit penalty step — {tier.name}</DialogTitle>
          <DialogDescription>
            memberScore = max(0, 100 − P × |Discipline Rating − Expectation Level|). Higher P
            penalises a rating/level mismatch more steeply. Default 25.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="penalty-step">Penalty step (P)</Label>
          <Input
            id="penalty-step"
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-invalid={!isValidDraft}
            className="max-w-[140px]"
          />
          {!isValidDraft && (
            <p className="text-xs text-destructive">Enter a whole number greater than 0.</p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!isValidDraft || setStep.isPending}
            onClick={() => setStep.mutate(parsedDraft, { onSuccess: () => onOpenChange(false) })}
          >
            {setStep.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TierPenaltyStepRow({ tier, canEdit }: { tier: TierWithDefaultShape; canEdit: boolean }) {
  const { data, isLoading, isError, error, refetch } = useTierExpectationPenaltyStep(tier.id);
  const [editOpen, setEditOpen] = useState(false);

  return (
    <>
      <tr className="border-t first:border-t-0">
        <td className="w-40 px-5 py-3 font-medium">{tier.name}</td>
        <td className="px-5 py-3">
          {isLoading ? (
            <Skeleton className="h-5 w-10" />
          ) : isError ? (
            <span className="inline-flex items-center gap-2 text-[12px] text-destructive">
              {error?.message ?? "Failed to load"}
              <button type="button" onClick={() => refetch()} className="underline">
                Retry
              </button>
            </span>
          ) : (
            <span className="font-display text-[15px] font-bold tabular-nums">
              {data?.expectationPenaltyStep ?? "—"}
            </span>
          )}
        </td>
        <td className="px-5 py-3 text-right">
          {canEdit && data && (
            <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="mr-1 h-3.5 w-3.5" />
              Edit
            </Button>
          )}
        </td>
      </tr>
      {editOpen && data && (
        <TierPenaltyStepEditDialog open={editOpen} onOpenChange={setEditOpen} tier={data} />
      )}
    </>
  );
}

export function TierScoringDefaultsCard({ tiers }: { tiers: TierWithDefaultShape[] }) {
  const { data: session } = useSession();
  const canEdit = hasCapability(session?.user?.role, "manageConfiguration");

  return (
    <section className="space-y-3">
      <div>
        <h3 className="font-display text-lg font-semibold">Expectation Match penalty step (P)</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Per-tier Configuration for the Fit Score Expectation dimension:{" "}
          <span className="font-mono text-xs">
            memberScore = max(0, 100 − P × |Discipline Rating − Expectation Level|)
          </span>
          . Default 25. Changing P never touches the formula itself — only how steeply a Discipline
          Rating / Expectation Level mismatch is penalised. Audited.
        </p>
      </div>
      <div className="rounded-lg border">
        <table className="w-full text-sm">
          <tbody>
            {tiers.map((tier) => (
              <TierPenaltyStepRow key={tier.id} tier={tier} canEdit={canEdit} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
